/**
 * PDF RENDERING (client)
 *
 * The single place that knows about pdf.js. Both the staff field mapper and the
 * parent-facing filler render the club's own PDF through here, so page geometry
 * and coordinate conversion are defined once.
 *
 * Coordinate contract, matching services/pdf-field-detect.js:
 *   rect {x, y, w, h}  fractions of page width/height, TOP-LEFT origin.
 * Fractions rather than points mean one stored map renders correctly at any zoom,
 * screen size, or device pixel ratio — and the same numbers drive the server-side
 * stamp, so what a parent sees is where the ink lands.
 */

/**
 * pdf.js is ~350 kB minified, and only two screens ever need it. Loading it on
 * first use keeps it out of the main bundle so every other page — and the kiosk's
 * first paint on an iPad — stays fast.
 *
 * The `.js` extension is required: this package.json declares "type": "module",
 * so webpack resolves src/ as strict ESM and will not guess extensions.
 */
let pdfjsPromise = null;
function getPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/legacy/build/pdf.js').then((lib) => {
      // The worker is copied into public/ by scripts/copy-pdf-worker.mjs at build
      // time. PUBLIC_URL keeps this right when served from a sub-path.
      lib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
      return lib;
    });
  }
  return pdfjsPromise;
}

/** Load a PDF from a URL, carrying auth so template PDFs behind auth still open. */
export async function loadPdf(url, { authToken } = {}) {
  const pdfjsLib = await getPdfjs();
  const params = { url, isEvalSupported: false };
  if (authToken) params.httpHeaders = { Authorization: `Bearer ${authToken}` };
  return pdfjsLib.getDocument(params).promise;
}

/** Load a PDF already in memory (bytes from an upload or a fetch). */
export async function loadPdfBytes(data) {
  const pdfjsLib = await getPdfjs();
  return pdfjsLib.getDocument({ data, isEvalSupported: false }).promise;
}

/**
 * Render one page into a canvas at a given CSS width.
 * Returns the page's CSS size so overlays can be positioned against it.
 */
export async function renderPage(pdf, pageNumber, canvas, cssWidth) {
  // pdf.js refuses to run two render operations against one canvas, and a resize
  // (rotating an iPad, the keyboard opening, a layout shift) can easily start a
  // second one mid-flight. Cancel whatever this canvas was doing first, or the
  // page ends up blank with no inputs drawn on it.
  if (canvas.__renderTask) {
    try {
      canvas.__renderTask.cancel();
    } catch {
      /* already settled */
    }
    canvas.__renderTask = null;
  }

  const page = await pdf.getPage(pageNumber);
  const base = page.getViewport({ scale: 1 });
  const scale = cssWidth / base.width;
  const viewport = page.getViewport({ scale });

  // Render at device resolution but lay out at CSS size, so text stays crisp on
  // retina screens and on an iPad at the front desk.
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.floor(viewport.width * dpr);
  canvas.height = Math.floor(viewport.height * dpr);
  canvas.style.width = `${Math.floor(viewport.width)}px`;
  canvas.style.height = `${Math.floor(viewport.height)}px`;

  const ctx = canvas.getContext('2d', { alpha: false });
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, viewport.width, viewport.height);

  const task = page.render({ canvasContext: ctx, viewport });
  canvas.__renderTask = task;
  try {
    await task.promise;
  } finally {
    if (canvas.__renderTask === task) canvas.__renderTask = null;
  }

  return {
    cssWidth: Math.floor(viewport.width),
    cssHeight: Math.floor(viewport.height),
    pdfWidth: base.width,
    pdfHeight: base.height,
  };
}

/** Page dimensions for every page, for laying out before render completes. */
export async function pageSizes(pdf) {
  const out = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const vp = page.getViewport({ scale: 1 });
    out.push({ width: vp.width, height: vp.height, aspect: vp.height / vp.width });
  }
  return out;
}

/** Normalized rect -> absolute CSS box within a rendered page of this size. */
export const rectToStyle = (rect, cssWidth, cssHeight) => ({
  left: `${rect.x * cssWidth}px`,
  top: `${rect.y * cssHeight}px`,
  width: `${rect.w * cssWidth}px`,
  height: `${rect.h * cssHeight}px`,
});

/** CSS box within a rendered page -> normalized rect, clamped to the page. */
export function styleToRect(box, cssWidth, cssHeight) {
  const clamp01 = (n) => Math.max(0, Math.min(1, n));
  const x = clamp01(box.left / cssWidth);
  const y = clamp01(box.top / cssHeight);
  return {
    x,
    y,
    w: clamp01(box.width / cssWidth) || 0.02,
    h: clamp01(box.height / cssHeight) || 0.01,
  };
}

/**
 * Font size in CSS pixels that matches how the server will stamp this field.
 * Keeps the on-screen input visually consistent with the printed result.
 */
export const fieldFontPx = (field, cssHeight, pdfHeight) => {
  const scale = cssHeight / pdfHeight;
  const fromPoints = (field.fontSize || 10) * scale;
  // Never exceed the box, and stay legible on a phone.
  return Math.max(9, Math.min(fromPoints, field.rect.h * cssHeight * 0.95));
};

const pdfRender = { loadPdf, loadPdfBytes, renderPage, pageSizes, rectToStyle, styleToRect, fieldFontPx };
export default pdfRender;
